import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import SpecialAttentionNotes from "components/QualityAssurance/Sections/Outline/SpecialAttentionNotes";
import InspectionComments from "components/QualityAssurance/Sections/Outline/InspectionComments";
import QATouches from "components/QualityAssurance/Sections/Outline/QATouches";
import CaseReturns from "components/QualityAssurance/Sections/Outline/CaseReturns";
import OrderInformation from "components/QualityAssurance/Sections/CaseDetails/OrderInformation";
import Coverage from "components/QualityAssurance/Sections/CaseDetails/Coverage";
import SubjectAdress from "components/QualityAssurance/Sections/CaseDetails/SubjectAddress";
import { getGpsData } from "requests/qualityAssurance/index.ts";
import { caseHasForm } from "selectors/homeowner/caseForms/caseForms";
import CallLogs from "components/Homeowner/ContactEfforts/CallLogs.tsx";
import { getDetailedHistoryCoalesced } from "requests/homeowner/caseforms";
import { MCard, MGridLayout } from "components/Shared";
import { QA_CASE_AUDIT_VIEW, QA_CASE_AUDIT_EDIT } from "typeConstants/rights";
import { userHasRight } from "selectors/rights";
import { QueueTypeEnum } from "interfaces/Mueller.Business.Models.ts";
import usePhoenixFormsSaveComplete from "components/PhoenixFormsV4/usePhoenixFormsSaveComplete.ts";
import HiddenFromQAMessage from "components/PhoenixFormsV4/HiddenFromQAMessage.tsx";

const Outline = (props) => {
  const mounted = useRef(true);
  const historyRequestCleanup = useRef(() => {});
  const [gpsData, setGpsData] = useState({ needsFetch: true, geoCoords: { latitude: 0, longitude: 0 } });
  const [history, setHistory] = useState();

  useEffect(() => {
    // TODO: Move to redux as this is fetched both here and in CaseDetails
    if (gpsData.needsFetch) {
      fetchGpsData();
    }
    historyRequestCleanup.current = getDetailedHistoryCoalesced(
      controlNumber,
      (data) => {
        if (mounted.current) {
          setHistory(data);
        }
      },
    );
    return () => {
      mounted.current = false;
      historyRequestCleanup.current();
    };
  }, []); 
  
  usePhoenixFormsSaveComplete(() => {    
    setGpsData({
      needsFetch : true,
      ...gpsData
    });
    fetchGpsData();
  });

  const fetchGpsData = () => {
    getGpsData(props.controlNumber, (s) => {
      setGpsData(s);
    }, mounted);
  };

  const {
    controlNumber,
    qaCase,
    qaCase: {
      caseInfo: {
        inspectionType,
        specialAttentionNotes,
        inspectionComments,
        qaTouches,
        caseReturns,
        customerNumber,
        customerName,
        policyNumber,
        policyholder,
        fieldRep,
        surveyAddress,
        atHomeStatus,
        surveyCounty,
        orderedDate,
        surveyDate,
        completedDate,
        trackingNumber,
        customerDueDate,
        caseType,
        governmentSponsorCode,
        primaryStatus,
        secondaryStatus,
        appraisalWorkflowStatus,
        transactionType,
        channel,
        stampsAndPhotoRequirementsLink,
        cubiCasaStatus,
      },
      controlIdNumber,
      dataVerifiedCaseForms,
    },
    displayAddressMap,
    canViewCoverageCard,
  } = props;

  let showCallLog = props.hasConvertForm || inspectionType === "CLOSE OUT";

  const filteredQaTouches = qaTouches?.filter(t => t.body?.length > 0);

  const rows = [
    ...(dataVerifiedCaseForms?.some(f => f.formCanBeHiddenFromQa) ? [[{ component: <HiddenFromQAMessage verifiedFormResults={dataVerifiedCaseForms} redTitle />}]] : []),
    ...(specialAttentionNotes ? [[{ component: <SpecialAttentionNotes text={specialAttentionNotes} /> }]] : []),
    ...(caseReturns?.length > 0 ? [[{ component: <CaseReturns caseReturns={caseReturns} /> }]] : []),
    ...(filteredQaTouches?.length > 0 ? [[{ component: <QATouches qaTouches={filteredQaTouches} /> }]] : []),
    [{
      component: (
        <OrderInformation
          trackingNumber={trackingNumber}
          controlNumber={controlNumber}
          reportType={inspectionType}
          originalSurveyType={qaCase.caseInfo.originalInspectionType}
          orderedDate={orderedDate}
          surveyDate={surveyDate}
          completedDate={completedDate}
          customerDueDate={customerDueDate}
          caseType={caseType}
          policyOrLoanNumber={policyNumber}
          customerName={customerName}
          customerNumber={customerNumber}
          governmentSponsor={governmentSponsorCode}
          primaryStatus={primaryStatus}
          webStatus={secondaryStatus}
          assignmentType={transactionType}
          AppraisalStatus={appraisalWorkflowStatus}
          Channel={channel}
          stampsAndPhotoRequirementsLink={stampsAndPhotoRequirementsLink}
          cubiCasaStatus={cubiCasaStatus}
          atHomeStatus={atHomeStatus}
          policyholder={policyholder}
          fieldRep={fieldRep}
        />
      ),
    }],
    [{
      component: (
        <SubjectAdress
          controlNumber={controlNumber}
          address={surveyAddress}
          county={surveyCounty}
          geocode={gpsData.geoCoords}
          onAddressClick={displayAddressMap}
        />
      ),
    }],
    ...(canViewCoverageCard ? [[{ component: <Coverage controlNumber={controlIdNumber} /> }]] : []),
    [{
      component: (
        <InspectionComments
          controlNumber={controlNumber}
          text={inspectionComments ? inspectionComments : ""}
          canUserEdit={true}
        />
      ),
    }],
    ...(showCallLog ? [[{
      component: (
        <MCard title="Call Log">
          <CallLogs
            controlNumber={controlNumber}
            tableData={history?.callLogs}
          />
        </MCard>
      ),
    }]] : []),
  ];

  return (
    <MGridLayout
      rows={rows}
      padding={0}
    />
  );
};

Outline.propTypes = {
  controlNumber: PropTypes.number.isRequired,
  qaCase: PropTypes.object.isRequired,
  displayAddressMap: PropTypes.func,
  canViewCoverageCard: PropTypes.bool,
  hasConvertForm: PropTypes.bool,
  canViewAuditWarningCard: PropTypes.bool,
};

const mapStateToProps = state => {
  return {
    qaCase: state.qualityAssurance.qaCase,
    hasConvertForm: caseHasForm("MUELLER_CONVERT_PNX")(state),
    canViewAuditWarningCard: (userHasRight(QA_CASE_AUDIT_VIEW)(state) && state.qualityAssurance.qaCase?.caseAuditCompleted)
      || (userHasRight(QA_CASE_AUDIT_EDIT)(state) && state.qualityAssurance.qaCase?.qaQueueTypeId === QueueTypeEnum.AuditQa),    
  };
};

export default connect(mapStateToProps)(Outline);
